import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { PermissionService } from 'src/app/services/masterpass/permission/permission.service';
import { ErrorHandlerService } from 'src/app/services/utilities/error-handler.service';
import { AuthActions, AuthApiActions, AuthEffectActions } from './auth.actions';
import { allSourcesFailed, mergePermissions } from './permissions.util';

/**
 * Async auth flows: resolving permissions and switching organisation.
 *
 * These used to live inside AuthService as hand-rolled subscriptions. Moving them
 * here makes the sequencing explicit and gives each step a cancellable, testable
 * boundary — the previous switchOrganisationToken subscribed without ever
 * unsubscribing.
 */
@Injectable()
export class AuthEffects {

  /**
   * Whether an organisation switch is mid-flight.
   *
   * The switch spans several requests, and only the last of them should trigger the
   * page reload. A flag is enough: switches are user-initiated, one at a time, and
   * the flow ends in a reload anyway.
   */
  private switchInProgress = false;

  constructor(
    private actions$: Actions,
    private store: Store,
    private authService: AuthService,
    private permissionService: PermissionService,
    private errorHandler: ErrorHandlerService,
  ) {}

  /**
   * Fetches organisation, department and role permissions and merges them.
   *
   * Each request catches its own failure so that one dead endpoint still leaves the
   * others usable — the behaviour the old `handleRequest` pipe provided. switchMap
   * means a second request supersedes the first, rather than two responses racing
   * to write the permission map as they used to.
   */
  loadPermissions$ = createEffect(() => this.actions$.pipe(
    ofType(AuthEffectActions.permissionsLoadRequested),
    switchMap(({ user }) => {
      const sources: Observable<any>[] = [
        this.permissionService.getUserOrganisationPermission(user.id).pipe(catchError(() => of(null))),
      ];
      if (user.department?.id) {
        sources.push(this.permissionService.getDepartmentPermissions(user.department.id).pipe(catchError(() => of(null))));
      }
      if (user.role?.id) {
        sources.push(this.permissionService.getRolePermissions(user.role.id).pipe(catchError(() => of(null))));
      }

      return forkJoin(sources).pipe(
        map(responses => {
          // Every source failing is a real failure, not a user with no permissions.
          // Reporting it as success would silently strip access with nothing shown.
          if (allSourcesFailed(responses)) {
            return AuthApiActions.permissionsLoadFailed({
              error: new Error('Every permission source failed to load'),
            });
          }
          return AuthApiActions.permissionsLoaded({ permissions: mergePermissions(responses) });
        }),
        catchError(error => of(AuthApiActions.permissionsLoadFailed({ error }))),
      );
    })
  ));

  /** Exchanges the current session for one scoped to another organisation. */
  switchOrganisation$ = createEffect(() => this.actions$.pipe(
    ofType(AuthEffectActions.organisationSwitchRequested),
    tap(() => this.switchInProgress = true),
    switchMap(({ organisation }) =>
      this.authService.switchOrganisation(organisation.id).pipe(
        map((res: any) => AuthApiActions.organisationSwitchSucceeded({
          token: res.data.accessToken,
          organisation,
        })),
        catchError(error => of(AuthApiActions.organisationSwitchFailed({ error }))),
      )
    )
  ));

  /**
   * Re-reads the profile under the new organisation's token.
   *
   * The switch response carries only a token, and role and department are
   * org-specific — reusing the cached user would resolve permissions against the
   * role held in the organisation just left. Reducers run before effects see an
   * action, so the new token is already in the store, and therefore on this request.
   */
  loadProfileAfterSwitch$ = createEffect(() => this.actions$.pipe(
    ofType(AuthApiActions.organisationSwitchSucceeded),
    switchMap(() => this.authService.fetchSelfProfile().pipe(
      switchMap((profile: any) => [
        AuthActions.userSet({ user: profile.data.user }),
        AuthEffectActions.permissionsLoadRequested({ user: profile.data.user }),
      ]),
      catchError(error => of(AuthApiActions.profileLoadFailed({ error }))),
    ))
  ));

  /**
   * Reloads once the switch has fully settled.
   *
   * Still load-bearing, unlike the reload sign-in used to do. Sign-in navigates from
   * /login, where the home screens have not mounted yet, so router navigation is
   * enough. An organisation switch happens with those screens already on-screen, and
   * around 68 of them read the user or organisation into a field during init — the
   * reload is what re-runs that. Removing it means making those components subscribe
   * instead, which is its own piece of work.
   *
   * One deliberate change: this waits for the permission load to finish. The old
   * code scheduled the reload 500ms after the fetch *started* — getPermissions
   * resolved as soon as it had subscribed, not when it had data — so a slow response
   * meant reloading into an empty permission map.
   */
  reloadAfterOrganisationSwitch$ = createEffect(() => this.actions$.pipe(
    ofType(
      AuthApiActions.permissionsLoaded,
      AuthApiActions.permissionsLoadFailed,
      AuthApiActions.profileLoadFailed,
    ),
    filter(() => this.switchInProgress),
    tap(() => {
      this.switchInProgress = false;
      setTimeout(() => this.reloadPage(), 500);
    })
  ), { dispatch: false });

  /** Seam for tests: window.location is not configurable, so it cannot be spied on. */
  protected reloadPage(): void {
    window.location.reload();
  }

  /** A failed switch leaves the session untouched, so there is nothing to resync. */
  clearSwitchOnFailure$ = createEffect(() => this.actions$.pipe(
    ofType(AuthApiActions.organisationSwitchFailed),
    tap(() => this.switchInProgress = false)
  ), { dispatch: false });

  /**
   * Surfaces failures the same way the old inline error callbacks did.
   *
   * handleHttpError reads e.error.error and e.error.message, so anything that is not
   * an HttpErrorResponse — a throw out of mergePermissions, say — would break it and
   * hide the original problem.
   */
  reportApiFailures$ = createEffect(() => this.actions$.pipe(
    ofType(
      AuthApiActions.organisationSwitchFailed,
      AuthApiActions.profileLoadFailed,
      AuthApiActions.permissionsLoadFailed,
    ),
    tap(({ error }) => {
      if (error instanceof HttpErrorResponse) this.errorHandler.handleHttpError(error);
      else console.error('[auth] ', error);
    })
  ), { dispatch: false });
}
